"use client";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { NexusCategoryConfig } from "@/types/nexus-product";

function toCategoryCode(category: string): string {
  return category
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

type Props = {
  categories: Array<
    NexusCategoryConfig & { image: string; productCount: number }
  >;
};

const formatProductCount = (count: number): string => {
  if (count === 1) return "1 produkt";
  if (count >= 2 && count <= 4) return `${count} produkty`;
  return `${count} produktów`;
};

const GraphicCategoryNav = ({ categories }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams() || new URLSearchParams();
  const pathname = usePathname();

  const selectedCodes = (searchParams.get("category") ?? "")
    .split(",")
    .filter(Boolean);

  const toggle = (code: string) => {
    const params = new URLSearchParams(searchParams.toString());
    const isSelected = selectedCodes.includes(code);

    if (isSelected) {
      const next = selectedCodes.filter((c) => c !== code);
      if (next.length) params.set("category", next.join(","));
      else params.delete("category");
    } else {
      params.set("category", [...selectedCodes, code].join(","));
    }

    params.set("page", "1");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
  };

  return (
    <div className="relative z-0 mb-6">
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
        {categories.map((cat) => {
          const code = toCategoryCode(cat.name);
          const isActive = selectedCodes.includes(code);

          return (
            <button
              key={cat.id}
              onClick={() => toggle(code)}
              className={`relative flex h-[92px] w-[190px] flex-shrink-0 items-center gap-3 rounded-lg border bg-white px-4 text-left shadow-1 outline outline-2 outline-offset-[-2px] transition-all duration-200 ease-out hover:z-20 hover:border-blue hover:outline-blue hover:shadow-2 focus-visible:z-20 sm:w-[210px] ${
                isActive
                  ? "z-10 border-blue outline-blue"
                  : "z-0 border-gray-3 outline-transparent"
              }`}
              aria-label={cat.name}
              title={cat.description || undefined}
            >
              <div className="relative h-[68px] w-[68px] flex-shrink-0 overflow-hidden rounded-md">
                <Image
                  src={cat.image}
                  alt={cat.name}
                  fill
                  sizes="68px"
                  className="object-cover"
                />
              </div>
              <span className="min-w-0">
                <span
                  className={`block truncate text-sm font-semibold transition-colors ${
                    isActive ? "text-blue" : "text-dark"
                  }`}
                >
                  {cat.name}
                </span>
                <span className="mt-1 block text-sm text-gray-6">
                  {formatProductCount(cat.productCount)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GraphicCategoryNav;
